import { useEffect, useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import api from "../../lib/api";

function StarRow({ rating }) {
    return (
        <div className="flex gap-0.5" aria-label={`Rated ${rating} out of 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    size={14}
                    strokeWidth={1.5}
                    className={i < rating ? "text-accent fill-accent" : "text-muted-foreground/40"}
                />
            ))}
        </div>
    );
}

export default function TestimonialsCarousel({
    variant = "light",
    featuredOnly = true,
    destinationName,
    autoplayMs = 6000,
    heading = "Voices from past journeys",
    overline = "Guest testimonials",
}) {
    const [items, setItems] = useState([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const timer = useRef(null);

    useEffect(() => {
        const params = {};
        if (featuredOnly && !destinationName) params.featured = true;
        if (destinationName) params.destination_name = destinationName;
        api.get("/testimonials", { params })
            .then((res) => setItems(res.data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [featuredOnly, destinationName]);

    const goTo = useCallback(
        (i) => {
            if (items.length === 0) return;
            setIndex(((i % items.length) + items.length) % items.length);
        },
        [items.length],
    );
    const next = useCallback(() => goTo(index + 1), [index, goTo]);
    const prev = useCallback(() => goTo(index - 1), [index, goTo]);

    useEffect(() => {
        if (items.length < 2 || !autoplayMs) return undefined;
        timer.current = setInterval(() => {
            setIndex((i) => (i + 1) % items.length);
        }, autoplayMs);
        return () => clearInterval(timer.current);
    }, [items.length, autoplayMs]);

    if (loading || items.length === 0) return null;

    const current = items[index];
    const isDark = variant === "dark";

    return (
        <section
            className={`section-padding ${isDark ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}
            data-testid="testimonials-carousel"
        >
            <div className="container-page">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <div>
                        <p className={`overline ${isDark ? "text-primary-foreground/60" : "text-muted-foreground"} mb-4`}>
                            {overline}
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-2xl">
                            {heading}
                        </h2>
                    </div>
                    {items.length > 1 && (
                        <div className="flex items-center gap-3" data-testid="testimonials-controls">
                            <button
                                type="button"
                                onClick={prev}
                                aria-label="Previous testimonial"
                                data-testid="testimonial-prev"
                                className={`w-11 h-11 border ${isDark ? "border-primary-foreground/30 hover:border-accent hover:text-accent" : "border-border hover:border-primary hover:text-primary"} flex items-center justify-center transition-colors`}
                            >
                                <ChevronLeft size={18} strokeWidth={1.5} />
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                aria-label="Next testimonial"
                                data-testid="testimonial-next"
                                className={`w-11 h-11 border ${isDark ? "border-primary-foreground/30 hover:border-accent hover:text-accent" : "border-border hover:border-primary hover:text-primary"} flex items-center justify-center transition-colors`}
                            >
                                <ChevronRight size={18} strokeWidth={1.5} />
                            </button>
                        </div>
                    )}
                </div>

                <article
                    className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 ${isDark ? "" : "bg-background"} p-8 sm:p-12 border ${isDark ? "border-primary-foreground/10" : "border-border"}`}
                    data-testid={`testimonial-card-${index}`}
                    key={current.id}
                >
                    <div className="lg:col-span-4 flex flex-col items-start gap-4">
                        {current.guest_photo_url && (
                            <img
                                src={current.guest_photo_url}
                                alt={current.guest_name}
                                className="w-28 h-28 object-cover rounded-full img-warm"
                                data-testid="testimonial-photo"
                            />
                        )}
                        <div>
                            <div className="font-serif text-2xl leading-snug" data-testid="testimonial-name">
                                {current.guest_name}
                            </div>
                            {current.trip_name && (
                                <div className={`text-xs mt-1 tracking-wider ${isDark ? "text-primary-foreground/65" : "text-muted-foreground"}`} data-testid="testimonial-trip">
                                    {current.trip_name}
                                </div>
                            )}
                            <div className="mt-3">
                                <StarRow rating={current.rating || 5} />
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-8">
                        <Quote
                            size={32}
                            strokeWidth={1.25}
                            className={isDark ? "text-accent mb-4" : "text-accent mb-4"}
                        />
                        <blockquote
                            className={`font-serif text-2xl sm:text-3xl leading-snug ${isDark ? "text-primary-foreground" : "text-foreground"}`}
                            data-testid="testimonial-quote"
                        >
                            &ldquo;{current.quote}&rdquo;
                        </blockquote>
                    </div>
                </article>

                {items.length > 1 && (
                    <div className="mt-8 flex justify-center gap-2" data-testid="testimonials-dots">
                        {items.map((it, i) => (
                            <button
                                key={it.id}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`Go to testimonial ${i + 1}`}
                                data-testid={`testimonial-dot-${i}`}
                                className={`h-1.5 transition-all ${
                                    i === index
                                        ? `w-10 ${isDark ? "bg-accent" : "bg-primary"}`
                                        : `w-4 ${isDark ? "bg-primary-foreground/30 hover:bg-primary-foreground/50" : "bg-foreground/20 hover:bg-foreground/40"}`
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
